import { useEffect, useState } from "react"

import { isEqual, round } from "lodash"
import { XIcon, Activity } from "lucide-react"
import { DefaultPopover, CubicBezier, cn, Separator, Input, ToggleOptionGroup, ToggleOptionGroupItem, DefaultTooltip } from "twyne-new.ui"

const CubicBezierPopover = ({ value, onChange, duration = 3000, inputProps = {} }) => {
  const [open, setOpen] = useState(false)

  return <>
    <DefaultPopover
      trigger={<div><CubicBezierInputPreview value={value} props={inputProps} /></div>}
      contentProps={{ className: 'w-fit flex flex-col gap-4', side: 'left', align: 'center' }}
      wrapperProps={{ open, onOpenChange: setOpen }}
      triggerProps={{ asChild: true, className: 'w-full' }}
    >
      <div className='w-full flex justify-between items-center'>
        Cubic Bezier
        <XIcon size='16' className='cursor-pointer' onClick={() => setOpen(false)} />
      </div>
      <Separator className='m-0 my-2' />

      <div className='flex gap-4 items-center justify-center'>
        <div className='grid grid-cols-2 gap-2 items-center justify-center'>
          <CubicBezierPreview type='linear' onClick={onChange} value={value} className='col-span-2 flex flex-col justify-center items-center' />
          <CubicBezierPreview type='ease' onClick={onChange} value={value} />
          <CubicBezierPreview type='ease-in' onClick={onChange} value={value} />
          <CubicBezierPreview type='ease-out' onClick={onChange} value={value} />
          <CubicBezierPreview type='ease-in-out' onClick={onChange} value={value} />
        </div>
        <CubicBezier value={value} onChange={onChange} duration={duration} height={260} width={260} />
      </div>
    </DefaultPopover>
  </>
}

const CubicBezierInputPreview = ({ value, props }) => <Input
  type='text'
  value={`cubic-bezier(${round(value[1].x, 2)},${round(value[1].y, 2)},${round(value[2].x, 2)},${round(value[2].y, 2)})`}
  readOnly
  classaName='w-full'
  {...props}
/>

const CubicBezierPreview = ({ type, onClick, value, className = '' }) => {
  const TYPE_VALUES = {
    linear: [{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 1 }],
    'ease-in': [{ x: 0, y: 0 }, { x: 0.42, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 1 }],
    'ease-out': [{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0.58, y: 1 }, { x: 1, y: 1 }],
    'ease-in-out': [{ x: 0, y: 0 }, { x: 0.42, y: 0 }, { x: 0.58, y: 1 }, { x: 1, y: 1 }],
    ease: [{ x: 0, y: 0 }, { x: 0.25, y: 0.1 }, { x: 0.25, y: 1 }, { x: 1, y: 1 }],
  }

  const isActive = isEqual(value, TYPE_VALUES[type])

  return <>
    <div
      onClick={() => onClick(TYPE_VALUES[type])}
      className={cn('p-2 twy-bg-base hover:twy-subtle-accent text-center flex flex-col items-center justify-center rounded-sm cursor-pointer transition-all duration-300', isActive && 'twy-subtle-accent', className)}>
      {type}
      <CubicBezier
        value={TYPE_VALUES[type]}
        duration={3000}
        height={50}
        width={50}
        pointSize={5}
        controlPointLineProps={{ className: 'opacity-0' }}
        backgroundLineProps={{ className: 'opacity-0' }}
        controlPointProps={{ className: 'opacity-0' }}
        indicatorProps={{ className: 'opacity-0' }}
      />
    </div>
  </>
}

const CubicBezierToggle = ({ value, onChange }) => <>
  <ToggleOptionGroup value={value ? 'cubic' : ''} type='single' onValueChange={onChange}>
    <ToggleOptionGroupItem value='cubic'>
      <DefaultTooltip content='Cubic bezier' triggerProps={{ className: 'w-full flex justify-center' }}>
        <Activity size='16' />
      </DefaultTooltip>
    </ToggleOptionGroupItem>
  </ToggleOptionGroup>
</>

const useCubicBezier = ({ value, easeOptions }) => {
  const [cubic, setCubic] = useState()

  useEffect(() => {
    if (cubic === undefined) {
      setCubic(value && !easeOptions.map(item => item.value).includes(value))
    }
  }, [cubic, value, easeOptions])

  return { cubic, onChangeCubic: setCubic }
}

export { CubicBezierPopover, CubicBezierPreview, useCubicBezier, CubicBezierToggle }
